"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import GeneralTab from "./GeneralTab";
import ChangeOrdersTab from "./ChangeOrdersTab";
import MaterialsTab3 from "./MaterialsTab3";
import ChangeOrdersTab2 from "./ChangeOrdersTab2";
import MaterialsTab from "./MaterialsTab";
import MaterialsTab2 from "./MaterialsTabV2";
import ChangeOrderV3Tab from "./ChangeOrderV3Tab";
import TimeAndMaterialTab3 from "./TimeAndMaterialTab3";
import WIPTab3 from "./WIPTab3";
import BudgetTab from "./BudgetTab";
import PieceworkRequestsTab from "./PieceworkRequestsTab";
import BudgetTab2 from "./BudgetTab2";
import BillsTab from "./BillsTab";
import EditPOsPanel from "./EditPOsPanel";
import EditWOsPanel from "./EditWOsPanel";
import BudgetTab3 from "./BudgetTab3";
import SOVTab3 from "./SOVTab3";
import SOVTab from "./SOVTab";
import SOVTab2 from "./SOVTab2";
import ProgressLogTab from "./ProgressLogTab";
import SharePointFilesTab from "./SharePointFilesTab";
import CorrespondenceTab from "./CorrespondenceTab";
import MetricsTab2 from "./MetricsTab2";
import MetricsTab from "./MetricsTab";
import LabourRatesTab from "./LabourRatesTab";
import PieceWorkerLogTab from "./PieceWorkerLogTab";
import PieceWorkerLogTab2 from "./PieceWorkerLogTab2";
import PieceworkerLogsTab3 from "./PieceworkerLogsTab3";
import TimeAndMaterialTab from "./TimeAndMaterialTab";
import TimeAndMaterialTabV2 from "./TimeAndMaterialTab2";
import DailyLogsTab from "./DailyLogsTab";
import SiteLogsTab from "./SiteLogsTab";
import RFIsTab from "./RFIsTab";
import TimesheetsTab from "./TimesheetTab";
import { useTabRefresh } from "./TabRefreshContext";
import { useTabs } from "./TabContext";
import WIPScreen from "./WIPTab";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import AlertDialog from "./AlertDialog";
import Select from "react-select";

const API = "https://erp-xe9u.onrender.com/api";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const BADGE_TONES = {
  green: {
    wrap: "bg-green-500/10 text-green-400 ring-green-500/20",
    dot: "fill-green-400",
  },
  yellow: {
    wrap: "bg-yellow-500/10 text-yellow-400 ring-yellow-500/20",
    dot: "fill-yellow-400",
  },
  blue: {
    wrap: "bg-blue-500/10 text-blue-400 ring-blue-500/20",
    dot: "fill-blue-400",
  },
  purple: {
    wrap: "bg-purple-500/10 text-purple-400 ring-purple-500/20",
    dot: "fill-purple-400",
  },
  orange: {
    wrap: "bg-orange-500/10 text-orange-400 ring-orange-500/20",
    dot: "fill-orange-400",
  },
  gray: {
    wrap: "bg-gray-500/10 text-gray-300 ring-gray-500/20",
    dot: "fill-gray-400",
  },
};

function Badge({ tone = "gray", dot = true, children }) {
  const t = BADGE_TONES[tone];
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-x-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        t.wrap,
      )}
    >
      {dot && (
        <svg
          viewBox="0 0 6 6"
          aria-hidden="true"
          className={classNames("size-1.5", t.dot)}
        >
          <circle r={3} cx={3} cy={3} />
        </svg>
      )}
      {children}
    </span>
  );
}

const projectStatusTone = (s) =>
  s === "active" ? "green" : s === "completed" ? "blue" : "yellow";

const versionTone = (v) => (v === 3 ? "orange" : v === 2 ? "blue" : "gray");

const woTypeTone = (type) =>
  type?.includes("piecework")
    ? "purple"
    : type?.includes("time")
      ? "blue"
      : type?.includes("material")
        ? "green"
        : "gray";

function IconButton({ onClick, title, tone = "gray", children }) {
  const tones = {
    gray: "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
    red: "text-red-400 hover:bg-red-500/10",
    orange: "text-orange-400 hover:bg-orange-500/10",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={classNames("rounded-lg p-1.5 transition-colors", tones[tone])}
    >
      {children}
    </button>
  );
}

const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-colors";

const btnSecondary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-200 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500/50 transition-colors disabled:cursor-not-allowed disabled:opacity-40";

const thBase =
  "whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-400";

function TableHeader({ title, subtitle, action }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-800 px-5 py-4 ring-1 ring-white/10">
      <div>
        <h2 className="text-lg font-semibold text-white">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-400">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function EmptyRow({ colSpan, label, actionLabel, onAction }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-14 text-center">
        <p className="text-sm font-semibold text-gray-200">{label}</p>
        <button
          onClick={onAction}
          className="mt-1 text-sm font-medium text-orange-400 hover:text-orange-300"
        >
          {actionLabel}
        </button>
      </td>
    </tr>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={btnSecondary}
      >
        <ChevronLeftIcon className="size-4" />
        Previous
      </button>
      <span className="text-sm text-gray-400">
        Page <span className="font-semibold text-gray-200">{currentPage}</span>{" "}
        of <span className="font-semibold text-gray-200">{totalPages}</span>
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={btnSecondary}
      >
        Next
        <ChevronRightIcon className="size-4" />
      </button>
    </div>
  );
}

const multiSelectFilterStyles = {
  control: (p, s) => ({
    ...p,
    backgroundColor: "#111827",
    borderColor: s.isFocused ? "#f97316" : "#374151",
    borderRadius: "0.5rem",
    boxShadow: s.isFocused ? "0 0 0 1px #f97316" : "none",
    minHeight: "42px",
    fontSize: "14px",
    "&:hover": { borderColor: "#f97316" },
  }),
  input: (p) => ({ ...p, color: "#e5e7eb" }),
  placeholder: (p) => ({ ...p, color: "#6b7280", fontSize: "14px" }),
  menuPortal: (b) => ({ ...b, zIndex: 999999 }),
  menu: (b) => ({
    ...b,
    backgroundColor: "#1f2937",
    borderRadius: "0.5rem",
    boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
    border: "1px solid #374151",
    width: "300px",
    maxWidth: "300px",
    minWidth: "260px",
    zIndex: 999999,
    overflow: "hidden",
  }),
  menuList: (b) => ({
    ...b,
    maxHeight: "380px",
    padding: "6px",
    overflowY: "auto",
  }),
  option: (b, s) => ({
    ...b,
    backgroundColor: s.isFocused ? "#374151" : "#1f2937",
    color: s.isSelected ? "#fb923c" : "#e5e7eb",
    padding: "10px 12px",
    cursor: "pointer",
    fontSize: "13.5px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: "1.4",
    borderRadius: "0.375rem",
  }),
  valueContainer: (p) => ({
    ...p,
    padding: "4px 8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    alignItems: "flex-start",
  }),
  multiValue: (b) => ({
    ...b,
    backgroundColor: "#374151",
    borderRadius: "6px",
    margin: 0,
    fontSize: "13px",
  }),
  multiValueLabel: (b) => ({
    ...b,
    color: "#e5e7eb",
    padding: "3px 8px",
    paddingRight: "6px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "150px",
  }),
  multiValueRemove: (b) => ({
    ...b,
    color: "#9ca3af",
    cursor: "pointer",
    borderRadius: "4px",
    ":hover": { backgroundColor: "#f97316", color: "white" },
  }),
};

export default function ProjectView({ handleViewOrder }) {
  const { id } = useParams();
  const location = useLocation();
  const focusKey = useTabRefresh();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!loading) window.scrollTo(0, 0);
  }, [loading]);

  const [pieceworkerLogs, setPieceworkerLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [purchaseOrdersV2, setPurchaseOrdersV2] = useState([]);
  const [isBudgetConfirmed, setIsBudgetConfirmed] = useState(false);
  const [hoveredTab, setHoveredTab] = useState(null);
  const [budgets2, setBudgets2] = useState([]);
  const [budgets2Loading, setBudgets2Loading] = useState(true);
  const [poCurrentPage, setPoCurrentPage] = useState(1);
  const [woCurrentPage, setWoCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const searchParams = new URLSearchParams(location.search);
  const { registerProjectName } = useTabs();

  useEffect(() => {
    if (project?.projectName) {
      registerProjectName(id, project.projectName);
    }
  }, [id, project?.projectName, registerProjectName]);

  // V3 state
  const isV3Project = project?.version === 3;
  const [purchaseOrdersV3, setPurchaseOrdersV3] = useState([]);
  const [showPOsPanel, setShowPOsPanel] = useState(false);
  const [workOrdersV3, setWorkOrdersV3] = useState([]);
  const [showWOsPanel, setShowWOsPanel] = useState(false);

  // WO sort / filter
  const [woSortField, setWoSortField] = useState("title");
  const [woSortDirection, setWoSortDirection] = useState("asc");
  const [woFilters, setWoFilters] = useState({
    title: [],
    type: [],
    woNumber: [],
    estimatedCost: [],
  });
  const [woFilterOpen, setWoFilterOpen] = useState(null);
  const woFilterRefs = useRef({});

  // Reset to page 1 whenever the WO filters or sort change
  useEffect(() => {
    setWoCurrentPage(1);
  }, [woFilters, woSortField, woSortDirection]);

  const tabFromUrl = searchParams.get("tab");
  const [activeStatusTab, setActiveStatusTab] = useState(
    searchParams.get("status") || location.state?.activeStatusTab || "Pending",
  );

  const [workOrdersV2, setWorkOrdersV2] = useState([]);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const userRole = localStorage.getItem("userRole");
  const [activeTab, setActiveTab] = useState(
    userRole === "foreman"
      ? "Worksheets"
      : tabFromUrl || location.state?.initialTab || "General",
  );

  const isV2Project = project?.version === 2;

  const [alert, setAlert] = useState({
    open: false,
    type: "warning",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });

  const closeAlert = () => setAlert((p) => ({ ...p, open: false }));

  const showErrorAlert = (title, message) =>
    setAlert({
      open: true,
      type: "error",
      title,
      message,
      onConfirm: closeAlert,
      showCancel: false,
    });

  const formatNumberWithCommas = (number) =>
    Number(number || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // ── Navigation ─────────────────────────────────────────────────────────────

  /** Generate PO: V3 → CreatePO3, V2 → v2 form, V1 → v1 form */
  const handleGeneratePO = () => {
    if (isV3Project) navigate(`/projects/${id}/purchase-orders3/new`);
    else if (isV2Project) navigate(`/Projects/${id}/purchase-orders2/new`);
    else navigate(`/projects/${id}/purchase-orders/new`);
  };

  /** View PO detail: V3 → PODetailView3, V2 → v2, V1 → v1 */
  const handleViewPO = (poId) => {
    if (isV3Project)
      navigate(`/projects/${id}/purchase-orders3/${poId}`, {
        state: { returnTab: "Purchase Orders" },
      });
    else if (isV2Project)
      navigate(`/projects/${id}/purchase-orders2/${poId}`, {
        state: { returnTab: "Purchase Orders" },
      });
    else
      navigate(`/projects/${id}/purchase-orders/${poId}`, {
        state: { returnTab: "Purchase Orders" },
      });
  };

  /** Generate WO: V3 → CreateWO3, V2 → v2 form, V1 → v1 form */
  const handleGenerateWO = () => {
    if (isV3Project) navigate(`/projects/${id}/work-orders3/new`);
    else if (isV2Project) navigate(`/Projects/${id}/work-orders2/new`);
    else navigate(`/Projects/${id}/purchase-orders/new`);
  };

  /** View WO detail: V3 → WorkOrderView3, V2 → v2, V1 → v1 */
  const handleViewWO = (woId) => {
    if (isV3Project)
      navigate(`/projects/${id}/work-orders3/${woId}`, {
        state: { returnTab: "Work Orders" },
      });
    else if (isV2Project) navigate(`/Projects/${id}/workorders2/${woId}`);
    else navigate(`/projects/${id}/workorders/${woId}`);
  };

  const handleViewLog = (logId) => {
    const basePath = isV2Project ? "/piecework-logs2" : "/piecework-logs";
    navigate(`${basePath}/${logId}`, {
      state: {
        fromProject: true,
        projectId: id,
        mainTab: "Operations",
        activeTab: "Worksheets",
        activeStatusTab,
      },
    });
  };

  const handleBackToProjects = () => navigate("/projects");

  // ── WO helpers ─────────────────────────────────────────────────────────────
  const calculateWOEstimatedCost = (wo) => {
    if (!wo.lineItems || !Array.isArray(wo.lineItems)) return 0;
    return wo.lineItems.reduce(
      (t, i) => t + (i.unitCost || 0) * (i.qtyEstimated || 0),
      0,
    );
  };

  const getWOEstimatedCost = (wo) => {
    if (isV3Project) return wo.originalEstimatedAmount ?? 0;
    if (isV2Project) return wo.estimatedTotal ?? 0;
    return calculateWOEstimatedCost(wo);
  };

  const handleWoSort = (field) => {
    if (woSortField === field)
      setWoSortDirection((p) => (p === "asc" ? "desc" : "asc"));
    else {
      setWoSortField(field);
      setWoSortDirection("asc");
    }
    setWoFilterOpen(null);
  };

  const formatWoType = (type) =>
    type
      ? type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "N/A";

  const allWorkOrders = isV3Project
    ? workOrdersV3
    : isV2Project
      ? workOrdersV2
      : project?.workOrders || [];

  const getUniqueWOValues = (field) =>
    [
      ...new Set(
        allWorkOrders.map((wo) => {
          if (field === "estimatedCost")
            return `$${formatNumberWithCommas(getWOEstimatedCost(wo))}`;
          if (field === "type") return formatWoType(wo.type);
          return wo[field] || "N/A";
        }),
      ),
    ].sort();

  const filteredAndSortedWorkOrders = [...allWorkOrders]
    .filter((wo) => {
      const checks = {
        title: wo.title || "N/A",
        type: formatWoType(wo.type),
        woNumber: wo.woNumber || "N/A",
        estimatedCost: `$${formatNumberWithCommas(getWOEstimatedCost(wo))}`,
      };
      return Object.entries(woFilters).every(
        ([f, sel]) => sel.length === 0 || sel.includes(checks[f]),
      );
    })
    .sort((a, b) => {
      let aVal, bVal;
      if (woSortField === "estimatedCost") {
        aVal = getWOEstimatedCost(a);
        bVal = getWOEstimatedCost(b);
      } else if (woSortField === "type") {
        aVal = (a.type || "").toLowerCase();
        bVal = (b.type || "").toLowerCase();
      } else {
        aVal = String(a[woSortField] || "").toLowerCase();
        bVal = String(b[woSortField] || "").toLowerCase();
      }
      if (aVal < bVal) return woSortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return woSortDirection === "asc" ? 1 : -1;
      return 0;
    });

  // ── Data fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const projectRes = await axios.get(`${API}/projects/${id}`, {
          headers,
          timeout: 10000,
        });
        const projectData = projectRes.data;

        if (projectData.version === 2) {
          try {
            const budgetRes = await axios.get(`${API}/budget2/${id}`, {
              headers,
              timeout: 10000,
            });
            if (budgetRes.data.success)
              projectData.budgets = budgetRes.data.budgets || [];
          } catch {
            projectData.budgets = projectData.budgets || [];
          }

          try {
            const poRes = await axios.get(
              `${API}/purchaseOrders2/project/${id}/all-summaries`,
              { headers },
            );
            setPurchaseOrdersV2(poRes.data.purchaseOrders || []);
          } catch {
            setPurchaseOrdersV2([]);
          }

          try {
            const woRes = await axios.get(
              `${API}/workorders2/project/${id}/with-net-totals`,
              { headers },
            );
            setWorkOrdersV2(woRes.data.workOrders || []);
          } catch {
            setWorkOrdersV2([]);
          }
        }

        if (projectData.version === 3) {
          try {
            const poRes = await axios.get(
              `${API}/purchaseOrders3/project/${id}`,
              { headers },
            );
            setPurchaseOrdersV3(poRes.data.purchaseOrders || []);
          } catch {
            setPurchaseOrdersV3([]);
          }

          try {
            const woRes = await axios.get(`${API}/workorders3/project/${id}`, {
              headers,
            });
            setWorkOrdersV3(woRes.data.workOrders || []);
          } catch {
            setWorkOrdersV3([]);
          }
        }

        setProject(projectData);
        setIsBudgetConfirmed(
          projectData.version === 3 ||
            projectData?.budgets?.[0]?.status === "confirmed" ||
            projectData?.budgets?.some((b) => b?.status === "confirmed"),
        );
        setLoading(false);
      } catch (err) {
        showErrorAlert("Error Loading Project", err.message);
        setLoading(false);
      }
    };
    fetchProject();
    if (location.state?.activeStatusTab)
      setActiveStatusTab(location.state.activeStatusTab);
  }, [id, location.state?.activeStatusTab, focusKey]);

  useEffect(() => {
    if (!budgets2Loading && budgets2.length > 0) {
      if (budgets2.some((b) => b?.status === "confirmed"))
        setIsBudgetConfirmed(true);
    }
  }, [budgets2, budgets2Loading]);

  useEffect(() => {
    if (activeTab === "Worksheets" && project && project.version !== 3) {
      const fetchLogs = async () => {
        setLogsLoading(true);
        try {
          const token = localStorage.getItem("token");
          const endpoint =
            project.version === 2
              ? `${API}/pieceworkerLogs2`
              : `${API}/pieceworkerLogs`;
          const res = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setPieceworkerLogs(res.data.filter((log) => log.project?._id === id));
        } catch {
          setLogsError("Failed to load pieceworker logs.");
        } finally {
          setLogsLoading(false);
        }
      };
      fetchLogs();
    }
  }, [activeTab, id, project?.version]);

  const handleBudgetUpdated = async (updatedProject) => {
    setProject(updatedProject);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    if (updatedProject?.version === 2) {
      try {
        const [budgetRes, poRes, netRes] = await Promise.all([
          axios.get(`${API}/budget2/${id}`, { headers, timeout: 10000 }),
          axios
            .get(`${API}/purchaseOrders2?project=${id}`, {
              headers,
              timeout: 10000,
            })
            .catch(() => ({ data: [] })),
          axios
            .get(`${API}/budget2/${id}/net-quantities`, {
              headers,
              timeout: 10000,
            })
            .catch(() => ({ data: { success: true, items: [] } })),
        ]);
        if (budgetRes.data.success) {
          setProject((prev) => ({
            ...prev,
            budgets: budgetRes.data.budgets || [],
            purchaseOrders: poRes.data || [],
            netQuantities: netRes.data.success ? netRes.data.items : [],
          }));
        }
      } catch (err) {
        console.log("Error refreshing v2 data:", err.message);
      }
    }

    if (updatedProject?.version === 3) {
      try {
        const [poRes, woRes] = await Promise.all([
          axios
            .get(`${API}/purchaseOrders3/project/${id}`, { headers })
            .catch(() => ({ data: { purchaseOrders: [] } })),
          axios
            .get(`${API}/workorders3/project/${id}`, { headers })
            .catch(() => ({ data: { workOrders: [] } })),
        ]);
        setPurchaseOrdersV3(poRes.data.purchaseOrders || []);
        setWorkOrdersV3(woRes.data.workOrders || []);
      } catch (err) {
        console.log("Error refreshing v3 data:", err.message);
      }
    }

    setIsBudgetConfirmed(
      updatedProject.version === 3 ||
        updatedProject.budgets?.some((b) => b?.status === "confirmed"),
    );
  };

  const handlePoRowClick = (poId, e) => {
    if (e.target.closest(".actions-column")) return;
    handleViewPO(poId);
  };
  const handleWoRowClick = (woId, e) => {
    if (e.target.closest(".actions-column")) return;
    handleViewWO(woId);
  };

  const handleDeletePOConfirm = (poId, poTitle) =>
    setAlert({
      open: true,
      type: "warning",
      title: "Confirm Delete",
      message: `Are you sure you want to delete "${poTitle || "Untitled"}"?`,
      onConfirm: () => {
        closeAlert();
        handleDeletePO(poId);
      },
      showCancel: true,
    });

  const handleDeletePO = async (poId) => {
    try {
      const token = localStorage.getItem("token");
      const endpoint = isV3Project
        ? `${API}/purchaseOrders3/${poId}`
        : isV2Project
          ? `${API}/purchaseOrders2/${poId}`
          : `${API}/purchaseOrders/${poId}`;
      const res = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      if (isV3Project)
        setPurchaseOrdersV3((p) => p.filter((po) => po._id !== poId));
      else if (isV2Project)
        setPurchaseOrdersV2((p) => p.filter((po) => po._id !== poId));
      else
        setProject((prev) => ({
          ...prev,
          purchaseOrders: prev.purchaseOrders.filter((po) => po._id !== poId),
        }));
      setDeleteMessage(res.data.message);
      setTimeout(() => setDeleteMessage(null), 5000);
    } catch (err) {
      showErrorAlert(
        "Error Deleting Purchase Order",
        err.response?.data?.message || err.message,
      );
    }
  };

  const calculatePOEstimatedCost = (po) => {
    if (isV3Project) return po.originalEstimatedAmount || 0;
    if (isV2Project) {
      if (po.items?.length)
        return po.items
          .filter((i) => !i.closedOutBySizeChange)
          .reduce((s, i) => s + (i.amount || 0), 0);
      return po.estimatedTotal || 0;
    }
    if (!po.lineItems?.length) return 0;
    return po.lineItems.reduce(
      (t, i) => t + (i.unitCost || 0) * (i.quantity || 0),
      0,
    );
  };

  const poSourceList = isV3Project
    ? purchaseOrdersV3
    : isV2Project
      ? purchaseOrdersV2
      : project?.purchaseOrders || [];
  const poTotalPages = Math.ceil((poSourceList.length || 0) / itemsPerPage);
  const paginatedPurchaseOrders = poSourceList.slice(
    (poCurrentPage - 1) * itemsPerPage,
    poCurrentPage * itemsPerPage,
  );

  // Close WO filter dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!woFilterOpen) return;
      const activeRef = woFilterRefs.current[woFilterOpen];
      if (activeRef?.contains(e.target)) return;
      const isRS =
        e.target.closest('[class*="css-"]') ||
        e.target.closest(".react-select__menu") ||
        e.target.id?.startsWith("react-select");
      if (isRS) return;
      setWoFilterOpen(null);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [woFilterOpen]);

  if (loading)
    return (
      <div className="py-24 text-center">
        <div className="inline-block size-8 animate-spin rounded-full border-b-2 border-orange-500" />
        <p className="mt-3 text-sm text-gray-400">Loading project...</p>
      </div>
    );
  if (!project)
    return (
      <>
        <div className="mx-auto max-w-xl py-16 text-center text-sm text-gray-400">
          Project could not be loaded.
        </div>
        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={closeAlert}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </>
    );

  // ── Tab config ─────────────────────────────────────────────────────────────
  const tabs =
    userRole === "foreman"
      ? [
          {
            name: "Operations",
            current: [
              "Worksheets",
              "Progress Logs",
              "Request for Information (RFI)",
              "Timesheets",
              "Time & Material",
              "Daily Logs",
              "Site Logs",
            ].includes(activeTab),
            subTabs: [
              "Worksheets",
              "Progress Logs",
              "Timesheets",
              "Request for Information (RFI)",
              "Time & Material",
              "Daily Logs",
              "Site Logs",
            ],
          },
          {
            name: "Materials",
            current: activeTab === "Materials",
            subTabs: [],
          },
          { name: "Files", current: activeTab === "Files", subTabs: [] },
        ]
      : isV3Project
        ? [
            { name: "General", current: activeTab === "General", subTabs: [] },
            {
              name: "Finances",
              current: [
                "Budget",
                "Purchase Orders",
                "Work Orders",
                "WIP",
                "Schedule of Values (Invoice)",
              ].includes(activeTab),
              subTabs: [
                "Budget",
                "Purchase Orders",
                "Work Orders",
                "WIP",
                "Schedule of Values (Invoice)",
              ],
            },
            {
              name: "Operations",
              current: [
                "Worksheets",
                "Site Requests",
                "Timesheets",
                "Change Order (CO)",
                "Time & Material",
                "Request for Information (RFI)",
                "Daily Logs",
                "Site Logs",
                "Labour Rates",
              ].includes(activeTab),
              subTabs: [
                "Worksheets",
                "Site Requests",
                "Timesheets",
                "Change Order (CO)",
                "Time & Material",
                "Request for Information (RFI)",
                "Daily Logs",
                "Site Logs",
                "Labour Rates",
              ],
            },
            {
              name: "Materials",
              current: activeTab === "Materials",
              subTabs: [],
            },
            { name: "Files", current: activeTab === "Files", subTabs: [] },
            { name: "Emails", current: activeTab === "Emails", subTabs: [] },
          ]
        : [
            { name: "General", current: activeTab === "General", subTabs: [] },
            {
              name: "Finances",
              current: [
                "Metrics",
                "Budget",
                "Purchase Orders",
                "Work Orders",
                "Bills",
                "Schedule of Values (Invoice)",
                "WIP",
              ].includes(activeTab),
              subTabs: [
                ...(!isV2Project ? ["Metrics"] : []),
                ...(isV2Project ? ["WIP"] : []),
                "Budget",
                "Purchase Orders",
                "Work Orders",
                "Bills",
                "Schedule of Values (Invoice)",
              ],
            },
            {
              name: "Operations",
              current: [
                "Worksheets",
                "Progress Logs",
                "Request for Information (RFI)",
                "Change Order (CO)",
                "Timesheets",
                "Time & Material",
                "Labour Rates",
                "Daily Logs",
                "Site Logs",
              ].includes(activeTab),
              subTabs: [
                "Worksheets",
                "Progress Logs",
                "Request for Information (RFI)",
                "Change Order (CO)",
                "Timesheets",
                "Time & Material",
                "Labour Rates",
                "Daily Logs",
                "Site Logs",
              ],
            },
            {
              name: "Materials",
              current: activeTab === "Materials",
              subTabs: [],
            },
            { name: "Files", current: activeTab === "Files", subTabs: [] },
            { name: "Emails", current: activeTab === "Emails", subTabs: [] },
          ];

  const isTabLocked = (tab) => {
    if (isV3Project) return false;
    return (
      !isBudgetConfirmed &&
      ![
        "General",
        "Budget",
        "Schedule of Values (Invoice)",
        "Files",
        "Metrics",
      ].includes(tab)
    );
  };

  const goToTab = (target) => {
    if (isTabLocked(target)) return;
    setActiveTab(target);
    const p = new URLSearchParams(location.search);
    p.set("tab", target);
    if (target === "Worksheets" && !location.state?.activeStatusTab) {
      setActiveStatusTab("Pending");
      p.set("status", "Pending");
    }
    navigate(`?${p.toString()}`, { replace: true });
    setHoveredTab(null);
  };

  const handleTabClick = (tabName, subTabName) =>
    goToTab(subTabName || tabName);

  const hasWoFilters = Object.values(woFilters).some((v) => v.length > 0);
  const woTotalPages = Math.ceil(
    filteredAndSortedWorkOrders.length / itemsPerPage,
  );

  const woColumns = [
    { label: "Title", field: "title" },
    { label: "WO Type", field: "type" },
    { label: "Estimated Cost", field: "estimatedCost" },
    { label: "WO Number", field: "woNumber" },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-full px-1 py-3 sm:px-4 sm:py-6">
      <div className="rounded-2xl bg-gray-900 p-3 shadow-xl ring-1 ring-white/10 sm:p-6">
        {deleteMessage && (
          <div className="mb-4 flex items-center justify-between gap-3 rounded-lg bg-green-500/10 px-4 py-3 text-sm text-green-300 ring-1 ring-green-500/20">
            <span>{deleteMessage}</span>
            <button
              onClick={() => setDeleteMessage(null)}
              className="text-green-300 hover:text-green-100"
              aria-label="Dismiss"
            >
              <XMarkIcon className="size-5" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-orange-400">
              Project
            </p>
            <h1 className="mt-1 truncate text-2xl font-bold text-white">
              {project.projectName}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {project.projectNumber && (
                <span className="rounded-full bg-gray-800 px-2.5 py-1 font-mono text-xs font-semibold text-orange-400 ring-1 ring-inset ring-white/10">
                  PN#{project.projectNumber}
                </span>
              )}

              <Badge tone={projectStatusTone(project.status)}>
                {(project.status || "planning").charAt(0).toUpperCase() +
                  (project.status || "planning").slice(1)}
              </Badge>
            </div>
          </div>
          <button
            onClick={handleBackToProjects}
            className={classNames(btnSecondary, "self-start shrink-0")}
          >
            <ArrowLeftIcon className="size-4" />
            Projects
          </button>
        </div>

        {/* Tab bar */}
        <div className="mb-6">
          <div className="grid grid-cols-1 sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => goToTab(e.target.value)}
              aria-label="Select a tab"
              className="col-start-1 row-start-1 w-full appearance-none rounded-lg bg-gray-800 py-2 pr-8 pl-3 text-base font-semibold text-gray-200 outline-1 -outline-offset-1 outline-gray-700 focus:outline-2 focus:-outline-offset-2 focus:outline-orange-400"
            >
              {tabs.map((tab) =>
                tab.subTabs.length > 0 ? (
                  <optgroup key={tab.name} label={`── ${tab.name} ──`}>
                    {tab.subTabs.map((sub) => (
                      <option key={sub} value={sub} disabled={isTabLocked(sub)}>
                        {sub}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  <option
                    key={tab.name}
                    value={tab.name}
                    disabled={isTabLocked(tab.name)}
                  >
                    {tab.name}
                  </option>
                ),
              )}
            </select>
            <ChevronDownIcon
              aria-hidden="true"
              className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-400"
            />
          </div>

          <div className="hidden sm:block">
            <nav className="relative z-[1] flex gap-1 rounded-xl bg-gray-800 p-1 ring-1 ring-white/10">
              {tabs.map((tab) => (
                <div
                  key={tab.name}
                  className="relative min-w-0 flex-1"
                  onMouseEnter={() =>
                    tab.subTabs.length > 0 && setHoveredTab(tab.name)
                  }
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  <button
                    onClick={() =>
                      handleTabClick(
                        tab.name,
                        tab.subTabs.length > 0 ? tab.subTabs[0] : tab.name,
                      )
                    }
                    disabled={isTabLocked(tab.name)}
                    aria-current={tab.current ? "page" : undefined}
                    className={classNames(
                      tab.current
                        ? "bg-orange-500 text-white shadow"
                        : isTabLocked(tab.name)
                          ? "cursor-not-allowed text-gray-600"
                          : "text-gray-400 hover:bg-gray-700 hover:text-gray-100",
                      "flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors",
                    )}
                  >
                    {tab.name}
                    {tab.subTabs.length > 0 && (
                      <ChevronDownIcon className="size-4 opacity-70" />
                    )}
                  </button>

                  {tab.subTabs.length > 0 && hoveredTab === tab.name && (
                    <div className="absolute left-0 top-full z-[10002] w-full min-w-[15rem] pt-2">
                      <div className="rounded-xl bg-gray-800 p-1.5 shadow-2xl ring-1 ring-white/10">
                        {tab.subTabs.map((sub) => (
                          <button
                            key={sub}
                            onClick={() => handleTabClick(tab.name, sub)}
                            disabled={isTabLocked(sub)}
                            className={classNames(
                              activeTab === sub
                                ? "bg-orange-500/15 text-orange-400"
                                : isTabLocked(sub)
                                  ? "cursor-not-allowed text-gray-600"
                                  : "text-gray-300 hover:bg-gray-700 hover:text-white",
                              "block w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors",
                            )}
                          >
                            {sub}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab content */}
        <div>
          {activeTab === "General" && (
            <GeneralTab project={project} setProject={setProject} />
          )}
          {activeTab === "Progress Logs" && (
            <ProgressLogTab project={project} setProject={setProject} />
          )}
          {activeTab === "Metrics" &&
            (isV2Project ? (
              <MetricsTab2 project={project} />
            ) : (
              <MetricsTab project={project} />
            ))}
          {activeTab === "Budget" &&
            (isV3Project ? (
              <BudgetTab3 project={project} setProject={handleBudgetUpdated} />
            ) : isV2Project ? (
              <BudgetTab2
                project={{
                  ...project,
                  budgets: project.budgets || [],
                  netQuantities: project.netQuantities || [],
                }}
                setProject={handleBudgetUpdated}
                onGeneratePO={handleGeneratePO}
              />
            ) : (
              <BudgetTab
                project={project}
                setProject={handleBudgetUpdated}
                onGeneratePO={handleGeneratePO}
              />
            ))}
          {activeTab === "Emails" && (
            <CorrespondenceTab project={project} setProject={setProject} />
          )}

          {/* ── Purchase Orders ── */}
          {activeTab === "Purchase Orders" && (
            <div>
              <TableHeader
                title="Purchase Orders"
                subtitle={
                  isV3Project
                    ? "One PO per floor — click a row to view line items filterable by section."
                    : null
                }
                action={
                  <button
                    onClick={
                      isV3Project
                        ? () => setShowPOsPanel(true)
                        : handleGeneratePO
                    }
                    className={btnPrimary}
                  >
                    {isV3Project ? (
                      <>
                        <PencilSquareIcon className="size-4" />
                        Edit POs
                      </>
                    ) : (
                      <>
                        <PlusIcon className="size-4" />
                        Generate PO
                      </>
                    )}
                  </button>
                }
              />

              <div className="overflow-hidden rounded-xl bg-gray-800 ring-1 ring-white/10">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-gray-800">
                      <tr>
                        <th className={thBase}>Title</th>
                        {isV3Project && <th className={thBase}>Sections</th>}
                        <th className={thBase}>Estimated Cost</th>
                        <th className={thBase}>PO Number</th>
                        <th
                          className={classNames(
                            thBase,
                            "actions-column text-right",
                          )}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {paginatedPurchaseOrders.length === 0 ? (
                        <EmptyRow
                          colSpan={isV3Project ? 5 : 4}
                          label="No purchase orders found"
                          actionLabel="Create your first purchase order"
                          onAction={
                            isV3Project
                              ? () => setShowPOsPanel(true)
                              : handleGeneratePO
                          }
                        />
                      ) : (
                        paginatedPurchaseOrders.map((po) => (
                          <tr
                            key={po._id}
                            onClick={(e) => handlePoRowClick(po._id, e)}
                            className="cursor-pointer transition-colors hover:bg-gray-700/50"
                          >
                            <td className="max-w-xs px-4 py-3 text-sm font-semibold text-gray-100">
                              <span className="block truncate">
                                {po.title || "Untitled"}
                              </span>
                            </td>
                            {isV3Project && (
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-400">
                                {po.sectionCount > 0 ? (
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="size-1.5 rounded-full bg-orange-400" />
                                    {po.sectionCount} section
                                    {po.sectionCount !== 1 ? "s" : ""}
                                  </span>
                                ) : (
                                  <span className="text-gray-600">—</span>
                                )}
                              </td>
                            )}
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-gray-100">
                              $
                              {formatNumberWithCommas(
                                calculatePOEstimatedCost(po),
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm">
                              {po.poNumber ? (
                                <span className="rounded-md bg-gray-900 px-2 py-0.5 font-mono text-xs text-orange-300 ring-1 ring-inset ring-white/10">
                                  #{po.poNumber}
                                </span>
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                            <td className="actions-column whitespace-nowrap px-4 py-3">
                              <div className="flex items-center justify-end gap-0.5">
                                <IconButton
                                  title="View"
                                  tone="orange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewPO(po._id);
                                  }}
                                >
                                  <EyeIcon className="size-5" />
                                </IconButton>
                                <IconButton
                                  title="Delete"
                                  tone="red"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePOConfirm(po._id, po.title);
                                  }}
                                >
                                  <TrashIcon className="size-5" />
                                </IconButton>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>

                    {poSourceList.length > 0 && (
                      <tfoot className="bg-gray-900">
                        <tr className="border-t-2 border-orange-500/40">
                          <td className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-300">
                            <span className="text-orange-400">
                              {poSourceList.length}
                            </span>{" "}
                            Purchase Order{poSourceList.length !== 1 ? "s" : ""}
                            {poTotalPages > 1 && (
                              <span className="ml-1 font-normal normal-case text-gray-500">
                                (page {poCurrentPage} of {poTotalPages})
                              </span>
                            )}
                          </td>
                          {isV3Project && (
                            <td className="px-4 py-3">
                              <p className="text-[10px] uppercase tracking-wide text-gray-400">
                                Total Sections
                              </p>
                              <p className="text-sm font-semibold text-white">
                                {poSourceList.reduce(
                                  (sum, po) => sum + (po.sectionCount || 0),
                                  0,
                                )}
                              </p>
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <p className="text-[10px] uppercase tracking-wide text-gray-400">
                              Total Estimated
                            </p>
                            <p className="font-mono text-sm font-bold text-orange-300">
                              $
                              {formatNumberWithCommas(
                                poSourceList.reduce(
                                  (sum, po) =>
                                    sum + calculatePOEstimatedCost(po),
                                  0,
                                ),
                              )}
                            </p>
                          </td>
                          <td className="px-4 py-3" />
                          <td className="px-4 py-3" />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {poTotalPages > 1 && (
                <Pagination
                  currentPage={poCurrentPage}
                  totalPages={poTotalPages}
                  onPageChange={setPoCurrentPage}
                />
              )}
            </div>
          )}

          {activeTab === "Materials" &&
            (isV3Project ? (
              <MaterialsTab3 project={project} setProject={setProject} />
            ) : isV2Project ? (
              <MaterialsTab2
                project={project}
                setProject={setProject}
                handleViewOrder={handleViewOrder}
              />
            ) : (
              <MaterialsTab
                project={project}
                setProject={setProject}
                handleViewOrder={handleViewOrder}
              />
            ))}

          {/* ── Work Orders ── */}
          {activeTab === "Work Orders" && (
            <div>
              <TableHeader
                title="Work Orders"
                subtitle={
                  isV3Project
                    ? "One WO per floor — type determines whether actuals come from timesheets or piecework sheets."
                    : null
                }
                action={
                  <button
                    onClick={
                      isV3Project
                        ? () => setShowWOsPanel(true)
                        : handleGenerateWO
                    }
                    className={btnPrimary}
                  >
                    {isV3Project ? (
                      <>
                        <PencilSquareIcon className="size-4" />
                        Edit Work Orders
                      </>
                    ) : (
                      <>
                        <PlusIcon className="size-4" />
                        Generate WO
                      </>
                    )}
                  </button>
                }
              />

              {/* Active filter chips */}
              {hasWoFilters && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-gray-800 px-4 py-2.5 ring-1 ring-white/10">
                  {Object.entries(woFilters).map(([field, selected]) =>
                    selected.length === 0 ? null : (
                      <span
                        key={field}
                        className="inline-flex items-center gap-1 rounded-full bg-orange-500/15 px-2.5 py-1 text-xs font-medium text-orange-300 ring-1 ring-inset ring-orange-500/30"
                      >
                        {field === "woNumber"
                          ? "WO #"
                          : field.charAt(0).toUpperCase() + field.slice(1)}
                        :{" "}
                        {selected.length === 1
                          ? selected[0].substring(0, 14) +
                            (selected[0].length > 14 ? "…" : "")
                          : `${selected.length} selected`}
                        <button
                          onClick={() =>
                            setWoFilters((p) => ({ ...p, [field]: [] }))
                          }
                          className="ml-0.5 hover:text-white"
                          aria-label={`Clear ${field} filter`}
                        >
                          <XMarkIcon className="size-3.5" />
                        </button>
                      </span>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setWoFilters({
                        title: [],
                        type: [],
                        woNumber: [],
                        estimatedCost: [],
                      })
                    }
                    className="rounded-md px-2 py-1 text-xs font-medium text-red-400 hover:bg-red-500/10"
                  >
                    Clear all
                  </button>
                  <span className="ml-auto text-xs text-gray-400">
                    Showing{" "}
                    <strong className="text-white">
                      {filteredAndSortedWorkOrders.length}
                    </strong>{" "}
                    work orders
                  </span>
                </div>
              )}

              {/* No overflow-hidden here so the filter dropdowns aren't clipped */}
              <div className="rounded-xl bg-gray-800 ring-1 ring-white/10">
                <table className="min-w-full divide-y divide-white/10">
                  <thead className="bg-gray-800">
                    <tr>
                      {woColumns.map(({ label, field }, idx) => (
                        <th
                          key={field}
                          className={classNames(
                            thBase,
                            woFilters[field]?.length > 0
                              ? "!text-orange-400"
                              : "",
                            idx === 0 ? "rounded-tl-xl" : "",
                          )}
                        >
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleWoSort(field)}
                              className="flex items-center gap-1 uppercase tracking-wide hover:text-orange-300"
                            >
                              {label}
                              <span className="text-xs text-gray-500">
                                {woSortField === field
                                  ? woSortDirection === "asc"
                                    ? "↑"
                                    : "↓"
                                  : "↕"}
                              </span>
                            </button>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWoFilterOpen((p) =>
                                    p === field ? null : field,
                                  );
                                }}
                                className={classNames(
                                  "ml-1 rounded px-1 py-0.5 text-[10px] transition-colors hover:bg-gray-700",
                                  woFilters[field]?.length > 0
                                    ? "text-orange-400"
                                    : "text-gray-500 hover:text-gray-300",
                                )}
                                aria-label={`Filter ${label}`}
                              >
                                ▼
                                {woFilters[field]?.length > 0 && (
                                  <span className="ml-0.5 rounded-full bg-orange-500 px-1 py-0.5 text-[9px] text-white">
                                    {woFilters[field].length}
                                  </span>
                                )}
                              </button>
                              {woFilterOpen === field && (
                                <div
                                  ref={(el) => {
                                    woFilterRefs.current[field] = el;
                                  }}
                                  className="absolute left-0 top-full z-[99999] mt-1 rounded-xl bg-gray-800 shadow-2xl ring-1 ring-white/10"
                                  style={{ minWidth: "280px" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="p-3">
                                    <Select
                                      isMulti
                                      closeMenuOnSelect={false}
                                      hideSelectedOptions={false}
                                      options={getUniqueWOValues(field).map(
                                        (v) => ({
                                          value: v,
                                          label: v,
                                        }),
                                      )}
                                      value={getUniqueWOValues(field)
                                        .map((v) => ({ value: v, label: v }))
                                        .filter((o) =>
                                          woFilters[field].includes(o.value),
                                        )}
                                      onChange={(opts) =>
                                        setWoFilters((p) => ({
                                          ...p,
                                          [field]: opts
                                            ? opts.map((o) => o.value)
                                            : [],
                                        }))
                                      }
                                      menuPortalTarget={document.body}
                                      styles={multiSelectFilterStyles}
                                      placeholder={`Filter ${label}...`}
                                    />
                                  </div>
                                  <div className="flex justify-end px-3 pb-2">
                                    <button
                                      onClick={() => {
                                        setWoFilters((p) => ({
                                          ...p,
                                          [field]: [],
                                        }));
                                        setWoFilterOpen(null);
                                      }}
                                      className="text-xs font-medium normal-case tracking-normal text-red-400 hover:text-red-300"
                                    >
                                      Clear
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </th>
                      ))}
                      {isV3Project && <th className={thBase}>Sections</th>}
                      <th
                        className={classNames(
                          thBase,
                          "actions-column rounded-tr-xl text-right",
                        )}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredAndSortedWorkOrders.length === 0 ? (
                      <EmptyRow
                        colSpan={isV3Project ? 6 : 5}
                        label="No work orders found"
                        actionLabel="Create your first work order"
                        onAction={
                          isV3Project
                            ? () => setShowWOsPanel(true)
                            : handleGenerateWO
                        }
                      />
                    ) : (
                      filteredAndSortedWorkOrders
                        .slice(
                          (woCurrentPage - 1) * itemsPerPage,
                          woCurrentPage * itemsPerPage,
                        )
                        .map((wo) => (
                          <tr
                            key={wo._id}
                            onClick={(e) => handleWoRowClick(wo._id, e)}
                            className="cursor-pointer transition-colors hover:bg-gray-700/50"
                          >
                            <td className="max-w-xs px-4 py-3 text-sm font-semibold text-gray-100">
                              <span className="block truncate">
                                {wo.title || "Untitled"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {wo.type ? (
                                <Badge tone={woTypeTone(wo.type)} dot={false}>
                                  {formatWoType(wo.type)}
                                </Badge>
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-gray-100">
                              ${formatNumberWithCommas(getWOEstimatedCost(wo))}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm">
                              {wo.woNumber ? (
                                <span className="rounded-md bg-gray-900 px-2 py-0.5 font-mono text-xs text-orange-300 ring-1 ring-inset ring-white/10">
                                  #{wo.woNumber}
                                </span>
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                            {isV3Project && (
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-400">
                                {wo.sectionCount > 0 ? (
                                  <span className="inline-flex items-center gap-1.5">
                                    <span className="size-1.5 rounded-full bg-orange-400" />
                                    {wo.sectionCount} section
                                    {wo.sectionCount !== 1 ? "s" : ""}
                                  </span>
                                ) : (
                                  <span className="text-gray-600">—</span>
                                )}
                              </td>
                            )}
                            <td className="actions-column whitespace-nowrap px-4 py-3">
                              <div className="flex items-center justify-end">
                                <IconButton
                                  title="View"
                                  tone="orange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewWO(wo._id);
                                  }}
                                >
                                  <EyeIcon className="size-5" />
                                </IconButton>
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>

                  {filteredAndSortedWorkOrders.length > 0 && (
                    <tfoot className="bg-gray-900">
                      <tr className="border-t-2 border-orange-500/40">
                        <td className="rounded-bl-xl px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-300">
                          <span className="text-orange-400">
                            {filteredAndSortedWorkOrders.length}
                          </span>{" "}
                          Work Order
                          {filteredAndSortedWorkOrders.length !== 1 ? "s" : ""}
                          {hasWoFilters && (
                            <span className="ml-1 font-normal normal-case text-gray-500">
                              (filtered)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3" />
                        <td className="px-4 py-3">
                          <p className="text-[10px] uppercase tracking-wide text-gray-400">
                            Total Estimated
                          </p>
                          <p className="font-mono text-sm font-bold text-orange-300">
                            $
                            {formatNumberWithCommas(
                              filteredAndSortedWorkOrders.reduce(
                                (sum, wo) => sum + getWOEstimatedCost(wo),
                                0,
                              ),
                            )}
                          </p>
                        </td>
                        <td className="px-4 py-3" />
                        {isV3Project && (
                          <td className="px-4 py-3">
                            <p className="text-[10px] uppercase tracking-wide text-gray-400">
                              Total Sections
                            </p>
                            <p className="text-sm font-semibold text-white">
                              {filteredAndSortedWorkOrders.reduce(
                                (sum, wo) => sum + (wo.sectionCount || 0),
                                0,
                              )}
                            </p>
                          </td>
                        )}
                        <td className="rounded-br-xl px-4 py-3" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {woTotalPages > 1 && (
                <Pagination
                  currentPage={woCurrentPage}
                  totalPages={woTotalPages}
                  onPageChange={setWoCurrentPage}
                />
              )}
            </div>
          )}

          {activeTab === "WIP" && isV3Project && <WIPTab3 project={project} />}
          {activeTab === "Request for Information (RFI)" && (
            <RFIsTab project={project} setProject={setProject} />
          )}
          {activeTab === "Change Order (CO)" &&
            (isV3Project ? (
              <ChangeOrderV3Tab project={project} />
            ) : isV2Project ? (
              <ChangeOrdersTab2 project={project} setProject={setProject} />
            ) : (
              <ChangeOrdersTab project={project} setProject={setProject} />
            ))}
          {activeTab === "Bills" && (
            <BillsTab project={project} setProject={setProject} />
          )}
          {activeTab === "Schedule of Values (Invoice)" &&
            (isV3Project ? (
              <SOVTab3 project={project} setProject={setProject} />
            ) : isV2Project ? (
              <SOVTab2 project={project} setProject={setProject} />
            ) : (
              <SOVTab project={project} setProject={setProject} />
            ))}
          {activeTab === "WIP" && isV2Project && <WIPScreen />}
          {activeTab === "Timesheets" && <TimesheetsTab project={project} />}
          {activeTab === "Files" && (
            <SharePointFilesTab
              projectId={id}
              projectName={project.projectName || ""}
              sharePointFolderName={project.sharePointFolderName || ""}
              setProject={setProject}
              createdAt={project.createdAt}
            />
          )}
          {activeTab === "Worksheets" && project.version === 1 && (
            <PieceWorkerLogTab
              project={project}
              setProject={setProject}
              pieceworkerLogs={pieceworkerLogs}
              logsLoading={logsLoading}
              logsError={logsError}
              onViewLog={handleViewLog}
              activeStatusTab={activeStatusTab}
              setActiveStatusTab={setActiveStatusTab}
            />
          )}
          {activeTab === "Worksheets" && project.version === 3 && (
            <PieceworkerLogsTab3 project={project} />
          )}
          {activeTab === "Site Requests" && project.version === 3 && (
            <PieceworkRequestsTab
              projectId={id}
              projectName={project.projectName}
              onLogCreated={() => {
                setActiveTab("Worksheets");
              }}
            />
          )}
          {activeTab === "Worksheets" && project.version === 2 && (
            <PieceWorkerLogTab2
              project={project}
              setProject={setProject}
              pieceworkerLogs={pieceworkerLogs}
              logsLoading={logsLoading}
              logsError={logsError}
              onViewLog={handleViewLog}
              activeStatusTab={activeStatusTab}
              setActiveStatusTab={setActiveStatusTab}
            />
          )}

          {activeTab === "Time & Material" &&
            (isV3Project ? (
              <TimeAndMaterialTab3 project={project} setProject={setProject} />
            ) : isV2Project ? (
              <TimeAndMaterialTabV2 project={project} setProject={setProject} />
            ) : (
              <TimeAndMaterialTab project={project} setProject={setProject} />
            ))}
          {activeTab === "Labour Rates" && (
            <LabourRatesTab project={project} setProject={setProject} />
          )}
          {activeTab === "Daily Logs" && (
            <DailyLogsTab project={project} setProject={setProject} />
          )}
          {activeTab === "Site Logs" && <SiteLogsTab project={project} />}
        </div>

        {showPOsPanel && isV3Project && (
          <EditPOsPanel
            projectId={id}
            token={localStorage.getItem("token")}
            initialCreate={true}
            onClose={() => setShowPOsPanel(false)}
            onSaved={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await axios.get(
                  `${API}/purchaseOrders3/project/${id}`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                  },
                );
                setPurchaseOrdersV3(res.data.purchaseOrders || []);
              } catch {
                /* silent */
              }
            }}
          />
        )}

        {showWOsPanel && isV3Project && (
          <EditWOsPanel
            projectId={id}
            token={localStorage.getItem("token")}
            unions={project?.unions || []}
            onClose={() => setShowWOsPanel(false)}
            onSaved={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await axios.get(
                  `${API}/workorders3/project/${id}`,
                  {
                    headers: { Authorization: `Bearer ${token}` },
                  },
                );
                setWorkOrdersV3(res.data.workOrders || []);
              } catch {
                /* silent */
              }
            }}
          />
        )}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={closeAlert}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
